import {Button} from "primereact/button";
import {IconField} from "primereact/iconfield";
import {InputIcon} from "primereact/inputicon";
import {InputText} from "primereact/inputtext";
import {Message} from "primereact/message";
import {useEffect, useRef, useState} from "react";

import {WebError} from "../api/web-error";
import {IconFieldLabel} from "../component/iconFieldChildren";
import {useRevaTranslation} from "../hooks/languageHooks";
import {selectUsername} from "../redux/authSlice";
import {redeemEmailVerificationThunk} from "../redux/emailVerificationRedeemThunk";
import {useRevaDispatch, useRevaSelector} from "../redux/revaHooks";
import {resolveUserApiErrorMessage} from "../util/errorUtil";
import {handlePreventDefault} from "../util/events";
import LoginCardHeader, {LOGIN_CARD_CLASS} from "./LoginCardHeader";

/**
 * #19351: Standalone page for the e-mail-verification link REVA sends (`/verify-email?token=…`), the REVA
 * counterpart of account-management's own JSF `verifyEmail.html`. Rendered directly by {@link AppContainer}
 * — deliberately OUTSIDE the login-gated `RouterProvider` (that router is only built once a user is logged
 * in, see `routerFactory`), so it stays reachable regardless of login state and is never hidden behind the
 * login form. Consequently it reads the token straight from `window.location` instead of a router hook (no
 * Router context is guaranteed here), and "to REVA" is a full navigation (`window.location.assign`), not a
 * client-side route change. Looks like the login card ({@link LoginCardHeader}, same float-label fields),
 * since users arrive here instead of the login — see `ResetPasswordPage` for the analogous "forgot
 * password" page this one was copied from.
 */
export default function VerifyEmailPage() {
    const {t, language} = useRevaTranslation();
    const dispatch = useRevaDispatch();

    const [token] = useState(() => new URLSearchParams(window.location.search).get("token"));

    // Prefill the username for a user who is (still, or already again) logged in — auto-login runs in the
    // background from app startup (see `index.tsx`) regardless of which page is shown, so the store's
    // username can arrive only after this component's first render. Tracked via a ref (not "field is still
    // empty") so that deliberately clearing the field — e.g. to correct a wrong prefilled account after a
    // 422 — does not snap the prefill back in.
    const storedUsername = useRevaSelector(selectUsername);
    const usernameTouched = useRef(false);
    const [username, setUsername] = useState("");
    useEffect(() => {
        if (storedUsername && !usernameTouched.current) setUsername(storedUsername);
    }, [storedUsername]);

    const [password, setPassword] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [result, setResult] = useState<"OK" | "CHANGED" | null>(null);

    const onSubmit = async () => {
        if (!token || !username.trim() || !password || submitting) return;
        setSubmitting(true);
        setError(null);
        try {
            const response = await dispatch(redeemEmailVerificationThunk(token, username.trim(), password));
            // The token is now used up (one-time link) — scrub it from the URL right away so it cannot
            // linger in the browser history/a bookmark.
            window.history.replaceState(null, "", window.location.pathname);
            setResult(response.result);
        } catch (e) {
            // The form stays (see the `token && !result` block below), so the user can correct a typo and
            // retry — but never with the previously entered password still in the field.
            setPassword("");
            if (e instanceof WebError && e.status === 429) {
                setError(t("verifyEmail.page.tooManyRequests"));
            } else {
                setError(
                    resolveUserApiErrorMessage(
                        e,
                        language,
                        t("verifyEmail.page.unavailable"),
                        t("general.error.unspecific"),
                    ),
                );
            }
        } finally {
            setSubmitting(false);
        }
    };

    const goToReva = () => window.location.assign("/");

    return (
        <div className="grow">
            <div className="flex items-center justify-center h-full">
                <form
                    className={`VerifyEmailPage ${LOGIN_CARD_CLASS}`}
                    onSubmit={handlePreventDefault(() => void onSubmit())}
                >
                    <LoginCardHeader subtitle={t("verifyEmail.page.header")} />

                    {!token && (
                        <Message
                            id="verifyEmailMissingToken"
                            severity="warn"
                            text={t("verifyEmail.page.missingToken")}
                            className="w-full"
                        />
                    )}

                    {token && result && (
                        <div className="flex flex-col gap-4">
                            <Message
                                id="verifyEmailSuccess"
                                severity="success"
                                text={t(
                                    result === "CHANGED"
                                        ? "verifyEmail.page.successChanged"
                                        : "verifyEmail.page.success",
                                )}
                                className="w-full"
                            />
                            <Button
                                id="verifyEmailToReva"
                                type="button"
                                className="w-full"
                                icon="pi pi-arrow-right"
                                label={t("verifyEmail.page.backToReva")}
                                onClick={goToReva}
                            />
                        </div>
                    )}

                    {token && !result && (
                        <div className="grid grid-cols-12 formgrid">
                            <div className="field col-span-12">
                                <p className="m-0 text-surface-600 dark:text-surface-300">
                                    {t("verifyEmail.page.explanation")}
                                </p>
                                <p className="mt-2 mb-0 text-sm text-surface-500 dark:text-surface-400">
                                    {t("verifyEmail.page.explanationHint")}
                                </p>
                            </div>
                            <div className="field col-span-12 mt-4">
                                <IconField iconPosition="left" className="p-float-label">
                                    <InputIcon className="pi pi-user" />
                                    <InputText
                                        id="verifyEmailUsername"
                                        value={username}
                                        className="w-full"
                                        type="text"
                                        autoComplete="username"
                                        disabled={submitting}
                                        onChange={(e) => {
                                            usernameTouched.current = true;
                                            setUsername(e.target.value);
                                        }}
                                    />
                                    <IconFieldLabel htmlFor="verifyEmailUsername" className="block font-medium">
                                        {t("verifyEmail.page.username")}
                                    </IconFieldLabel>
                                </IconField>
                            </div>
                            <div className="field col-span-12 mt-4">
                                <IconField iconPosition="left" className="p-float-label">
                                    <InputIcon className="pi pi-lock" />
                                    <InputText
                                        id="verifyEmailPassword"
                                        value={password}
                                        className="w-full"
                                        type="password"
                                        autoComplete="current-password"
                                        disabled={submitting}
                                        onChange={(e) => setPassword(e.target.value)}
                                    />
                                    <IconFieldLabel htmlFor="verifyEmailPassword" className="block font-medium">
                                        {t("verifyEmail.page.password")}
                                    </IconFieldLabel>
                                </IconField>
                            </div>
                            <div className="field col-span-12 mt-6">
                                <Button
                                    id="verifyEmailSubmitBtn"
                                    className="w-full"
                                    icon={submitting ? "pi pi-spin pi-spinner" : "pi pi-check"}
                                    label={t("verifyEmail.page.submit")}
                                    disabled={submitting || !username.trim() || !password}
                                />
                            </div>
                            {error && (
                                <div className="field col-span-12">
                                    <Message id="verifyEmailError" severity="error" text={error} className="w-full" />
                                </div>
                            )}
                            <div className="field col-span-12 mt-2 !mb-0 text-right">
                                <a id="verifyEmailToLoginLink" href="/">
                                    {t("verifyEmail.page.backToReva")}
                                </a>
                            </div>
                        </div>
                    )}
                </form>
            </div>
        </div>
    );
}
